import type { DatePeriod } from "@repo/shared/types";
import { OLDER_THAN_DATE_PERIODS, WITHIN_DATE_PERIODS } from "@repo/shared/types";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DraftFilterValue, FilterField } from "./field-registry";
import { getFilterValueLabels } from "./value-labels";

/** How many picked values a picker button names before it switches to a count. */
const MAX_NAMED_VALUES = 2;

/**
 * QL-349 — the value-editing control for one filter row, dispatched by
 * `field.kind`. Shared by the Settings view editor and the ad-hoc filter bar
 * on `/clients` — the one place each filter kind's input is built.
 *
 * QL-512 — every kind is exactly one control: a select, a number input, or a
 * button that opens the value picker.
 */
export function FilterValueEditor({
  field,
  draft,
  onChange,
  onOpenValuesDialog,
  userLanguage,
}: {
  field: FilterField;
  draft: DraftFilterValue;
  onChange: (patch: DraftFilterValue) => void;
  onOpenValuesDialog: () => void;
  userLanguage: string;
}) {
  const t = getFilterValueLabels(userLanguage);
  const labelOf = (v: string) =>
    field.options?.find((o) => o.value === v)?.label || field.unresolvedLabel || v;

  switch (field.kind) {
    case "isAnyOf":
    case "containsAnyOf": {
      const values = draft.values ?? [];
      const summary =
        values.length === 0
          ? t.noValuesSelected
          : values.length <= MAX_NAMED_VALUES
            ? values.map(labelOf).join(", ")
            : t.selected(values.length);
      return (
        <Button
          type="button"
          variant="outline"
          onClick={onOpenValuesDialog}
          className="w-full min-w-[12rem] justify-between font-normal"
        >
          <span className={values.length === 0 ? "truncate text-muted-foreground" : "truncate"}>
            {summary}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      );
    }

    case "favourite":
      return <p className="text-sm text-muted-foreground">{t.favouriteHint}</p>;

    case "empty":
      return (
        <Select
          value={draft.operator ?? ""}
          onValueChange={(value) => onChange({ operator: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="empty">{t.isEmpty}</SelectItem>
            <SelectItem value="notEmpty">{t.isNotEmpty}</SelectItem>
          </SelectContent>
        </Select>
      );

    case "boolean":
      return (
        <Select value={draft.value ?? ""} onValueChange={(value) => onChange({ value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{t.yes}</SelectItem>
            <SelectItem value="false">{t.no}</SelectItem>
          </SelectContent>
        </Select>
      );

    case "datePeriod":
      return (
        <Select
          value={draft.period ?? ""}
          onValueChange={(value) => onChange({ period: value as DatePeriod })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WITHIN_DATE_PERIODS.map((period) => (
              <SelectItem key={period} value={period}>
                {t.periodLabel(period)}
              </SelectItem>
            ))}
            <SelectSeparator />
            {OLDER_THAN_DATE_PERIODS.map((period) => (
              <SelectItem key={period} value={period}>
                {t.periodLabel(period)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "amount":
      return (
        <Input
          type="number"
          inputMode="decimal"
          className="w-36"
          value={draft.value ?? ""}
          onChange={(e) => onChange({ value: e.target.value })}
        />
      );
  }
}
